<!-- Koel's existing Vue adapter retains every source filter, sort, selection,
     import/report, file restore and context action. See KoelLibrary.vue for MIT
     attribution. No library or playback state is copied for this flavor. -->
<template>
  <section class="apple-page apple-library" :data-library-workspace="workspaceMode" :data-library-tab="$route.query.tab || initialTab" data-apple-library aria-label="音乐库">
    <div v-if="workspaceMode === 'local' && !['songs','manage','playHistory','recentAdded','albums','artists','playlists','folders','cloudDisk'].includes($route.query.tab || initialTab)" class="apple-library-imports" aria-label="本地音乐管理">
      <router-link class="apple-page-action" to="/library?tab=manage">管理存储 <span aria-hidden="true">›</span></router-link>
      <span class="apple-import-spacer" />
      <button class="apple-page-action" type="button" :disabled="importRunning" @click="chooseImport('folder')">导入文件夹</button>
      <button class="apple-page-filled" type="button" :disabled="importRunning" @click="chooseImport('files')"><svg-icon icon-class="plus" />导入音乐</button>
    </div>
    <KoelLibrary ref="library" apple-presentation :workspace-mode="workspaceMode" :webdav-as-local="webdavAsLocal" :initial-tab="initialTab" @request-workspace="$emit('request-workspace', $event)" />
  </section>
</template>
<script>
import KoelLibrary from '../salt/KoelLibrary.vue';
import AppleLibraryContent from './AppleLibraryContent.vue';
import AppleSongTable from './ui/AppleSongTable.vue';
import AppleCloudPanel from './AppleCloudPanel.vue';
import AppleLibraryPlaylists from './AppleLibraryPlaylists.vue';
import AppleWebDavSettings from './AppleWebDavSettings.vue';
import { runtime } from '../bridge';
import { libraryAddedAt } from '../library-sort';
import './apple-pages.scss';
import './apple-library-redesign.scss';
import './apple-library-recent.scss';
const padDate = value => String(value).padStart(2, '0');
const dateLabel = value => {
  const time = libraryAddedAt(value);
  if (time === null) return '时间未记录';
  const date = new Date(time);
  return `${date.getFullYear()}-${padDate(date.getMonth() + 1)}-${padDate(date.getDate())} ${padDate(date.getHours())}:${padDate(date.getMinutes())}`;
};
const historyDateLabel = (value, now) => {
  const time = Number(value), date = new Date(time), today = new Date(now || Date.now());
  if (!Number.isFinite(time) || time <= 0) return '时间未记录';
  today.setHours(0, 0, 0, 0);
  if (time >= today.getTime()) { const minutes = Math.max(0, Math.floor(((now || Date.now()) - time) / 60000)); return minutes < 1 ? '刚刚' : minutes < 60 ? minutes + ' 分钟前' : Math.floor(minutes / 60) + ' 小时前'; }
  const clock = date.toLocaleTimeString('zh-CN', { hour:'2-digit', minute:'2-digit', hour12:false });
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  return time >= yesterday.getTime() ? '昨天 ' + clock : (date.getMonth() + 1) + '月' + date.getDate() + '日';
};
// A functional adapter forwards refs/listeners to the existing table. Only the
// two timestamp columns vary; no added date is substituted for playback time.
const LibrarySongTable = {
  functional: true,
  render(h, { data, parent, children }) {
    const added = parent.currentTab === 'recentAdded', history = parent.currentTab === 'playHistory';
    const props = { ...data.attrs, ...data.props };
    if (added || history) Object.assign(props, { selectable:true, showFavorites:history, showNumber:false, showHeader:!history, statusSortable:added, sortOrder:added ? parent.recentAddedDirection : props.sortOrder, statusLabel: added ? '添加时间' : '播放时间', statusWidth: history ? '132px' : '180px', statusForTrack: track => added ? dateLabel(track.createdAt) : historyDateLabel(parent.state.historyPlayedAt[track.id], parent.historyNow) });
    return h(AppleSongTable, { ...data, props }, children);
  },
};
const AppleKoelLibrary = {
  extends: AppleLibraryContent,
  components: { KoelSongList: LibrarySongTable, NeteaseCloudPanel: AppleCloudPanel, WebDavConnections: AppleWebDavSettings, SharedPlaylistPanel: AppleLibraryPlaylists },
  methods: { folderPath(folder) { const first = folder?.tracks?.[0]; return first ? runtime.folderName(first) : folder?.name || '浏览器文件'; } },
  computed: {
    historyGroups() {
      if (this.currentTab !== 'playHistory') return [];
      const today = new Date(this.historyNow || Date.now()); today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
      const groups = new Map();
      for (const track of this.displayTracks) {
        const time = Number(this.state.historyPlayedAt[track.id]);
        const label = Number.isFinite(time) && time > 0 ? time >= today.getTime() ? '今天' : time >= yesterday.getTime() ? '昨天' : '更早' : '时间未记录';
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(track);
      }
      return [...groups].map(([label, tracks]) => ({ label, tracks }));
    },
    folderGroups() {
      const groups = new Map();
      this.sourceFilteredTracks.filter(track => track.sourceType === 'local').forEach(track => { const name = runtime.folderName(track); if (!groups.has(name)) groups.set(name,{name,tracks:[]}); groups.get(name).tracks.push(track); });
      return [...groups.values()].sort((a,b) => a.name.localeCompare(b.name,'zh-CN'));
    },
    shownCategories() {
      const tabs = KoelLibrary.computed.shownCategories.call(this).map(tab => ({ ...tab, label: tab.id === 'playlists' ? '歌单' : tab.id === 'manage' ? '管理' : tab.label }));
      if (!tabs.some(tab => tab.id === 'recentAdded')) {
        const manageIndex = tabs.findIndex(tab => tab.id === 'manage');
        tabs.splice(manageIndex < 0 ? tabs.length : manageIndex, 0, { id: 'recentAdded', label: '最近添加' });
      }
      return tabs;
    },
  },
};
export default {
  name: 'AppleLibrary', components: { KoelLibrary: AppleKoelLibrary },
  props: { workspaceMode: { type: String, default: 'all' }, webdavAsLocal: { type: Boolean, default: true }, initialTab: { type: String, default: 'songs' } },
  data() { return { state: runtime.state }; },
  computed: { importRunning() { return this.state.importTask?.status === 'running'; } },
  methods: { chooseImport(kind) { this.$refs.library?.chooseImport(kind); } },
};
</script>
